import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Client } from '../../entities/client';
import { getClients } from '../../entities/client/api/clientApi';
import { createTicket } from '../../entities/ticket/api/ticketApi';
import { TicketSite } from '../../entities/ticket';
import { SendEmailModal } from '../../features/client-email';
import { EditClientModal } from '../../features/client-edit';
import { ClientTicketsModal } from '../../features/client-tickets';
import { HudleStaffActions } from '../../features/hudle-staff/HudleStaffActions';
import * as S from './ClientsPage.styles';
import {S3Icon} from "../../shared/ui";

export const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTicketsModalOpen, setIsTicketsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const clientsPerPage = 50;

  useEffect(() => {
    loadClients();
  }, [currentPage, selectedSite]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * clientsPerPage;
      const data = await getClients({ 
        search: searchQuery || undefined,
        site: selectedSite !== 'all' ? selectedSite : undefined,
        limit: clientsPerPage,
        offset,
      });
      setClients(data.results);
      setTotalCount(data.count);
    } catch (error) {
      console.error('Ошибка загрузки клиентов:', error);
      alert('Не удалось загрузить клиентов');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    await loadClients();
  };

  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSite(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getSiteLabel = (site?: string): string => {
    if (!site) return 'Не указан';
    if (site === 'hudle') return 'Hudle';
    if (site === 'tournament') return 'Турнир';
    if (site === 'unknown') return 'Неизвестен';
    return site;
  };

  const getClientTypeLabel = (type: string): string => {
    const types: Record<string, string> = {
      'individual': 'Физическое лицо',
      'company': 'Компания',
      'organization': 'Организация',
    };
    return types[type] || type;
  };

  const getAllContacts = (client: Client) => {
    const contacts = client.contacts || [];
    if (!client.mainContact) {
      return contacts;
    }
    const mainContactInList = contacts.some(c => c.id === client.mainContact?.id);
    if (mainContactInList) {
      return contacts;
    }
    return [client.mainContact, ...contacts];
  };

  const handleEmailClick = () => {
    if (!selectedClient?.mainContact?.email) {
      alert('У клиента нет email адреса');
      return;
    }
    setIsEmailModalOpen(true);
  };

  const handleCreateTicketClick = async () => {
    if (!selectedClient) return;
    
    try {
      const site = (selectedClient.externalSite as TicketSite) || 'unknown';
      const ticket = await createTicket({
        title: `Новый тикет для ${selectedClient.name}`,
        site,
        clientId: selectedClient.id,
        clientName: selectedClient.name,
        clientEmail: selectedClient.mainContact?.email || '',
        priority: 'medium',
      });
      navigate(`/tickets/${ticket.id}`);
    } catch (error) {
      console.error('Ошибка создания тикета:', error);
      alert('Не удалось создать тикет');
    }
  };

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleTicketsClick = () => {
    setIsTicketsModalOpen(true);
  };

  const handleClientUpdated = (updatedClient: Client) => {
    setSelectedClient(updatedClient);
    loadClients();
  };

  return (
    <S.ClientsContainer>
      <S.ClientsHeader>
        <S.ClientsTitle>Клиенты</S.ClientsTitle>
        <S.HeaderActions>
          <S.SearchForm onSubmit={handleSearchSubmit}>
            <S.SearchInput
              type="text"
              placeholder="Поиск по имени, email или UID..."
              value={searchQuery}
              onChange={handleSearch}
            />
            <S.SearchButton type="submit">Поиск</S.SearchButton>
          </S.SearchForm>
          <S.SiteFilter>
            <S.SiteFilterLabel>Сайт:</S.SiteFilterLabel>
            <S.SiteFilterSelect
              value={selectedSite}
              onChange={handleSiteChange}
            >
              <option value="all">Все</option>
              <option value="hudle">Hudle</option>
              <option value="tournament">Турнир</option>
              <option value="unknown">Неизвестен</option>
            </S.SiteFilterSelect>
          </S.SiteFilter>
        </S.HeaderActions>
      </S.ClientsHeader>

      <S.ClientsContent>
        {loading && clients.length === 0 ? (
          <S.EmptyState>
            <S.EmptyStateText>Загрузка...</S.EmptyStateText>
          </S.EmptyState>
        ) : clients.length === 0 ? (
          <S.EmptyState>
            <S.EmptyStateText>Клиенты не найдены</S.EmptyStateText>
            <S.EmptyStateSubtext>
              Попробуйте изменить параметры поиска
            </S.EmptyStateSubtext>
          </S.EmptyState>
        ) : (
          <>
            <S.ClientsList>
              {clients.map(client => (
              <S.ClientCard
                key={client.id}
                onClick={() => setSelectedClient(client)}
                $selected={selectedClient?.id === client.id}
              >
                <S.ClientCardHeader>
                  <S.ClientCardName>{client.name}</S.ClientCardName>
                  <S.ClientCardSite>{getSiteLabel(client.externalSite)}</S.ClientCardSite>
                </S.ClientCardHeader>
                <S.ClientCardInfo>
                  <S.ClientInfoRow>
                    <S.ClientInfoLabel>Тип:</S.ClientInfoLabel>
                    <S.ClientInfoValue>{getClientTypeLabel(client.type)}</S.ClientInfoValue>
                  </S.ClientInfoRow>
                  {client.mainContact && (
                    <S.ClientInfoRow>
                      <S.ClientInfoLabel>Email:</S.ClientInfoLabel>
                      <S.ClientInfoValue>{client.mainContact.email}</S.ClientInfoValue>
                    </S.ClientInfoRow>
                  )}
                  {client.mainContact?.name && (
                    <S.ClientInfoRow>
                      <S.ClientInfoLabel>Контакт:</S.ClientInfoLabel>
                      <S.ClientInfoValue>{client.mainContact.name}</S.ClientInfoValue>
                    </S.ClientInfoRow>
                  )}
                  {client.uid && (
                    <S.ClientInfoRow>
                      <S.ClientInfoLabel>UID:</S.ClientInfoLabel>
                      <S.ClientInfoValue>{client.uid}</S.ClientInfoValue>
                    </S.ClientInfoRow>
                  )}
                  {client.region && (
                    <S.ClientInfoRow>
                      <S.ClientInfoLabel>Регион:</S.ClientInfoLabel>
                      <S.ClientInfoValue>{client.region}</S.ClientInfoValue>
                    </S.ClientInfoRow>
                  )}
                </S.ClientCardInfo>
                {getAllContacts(client).length > 1 && (
                  <S.ClientContactsCount>
                    Контактов: {getAllContacts(client).length}
                  </S.ClientContactsCount>
                )}
              </S.ClientCard>
              ))}
            </S.ClientsList>
            {totalCount > clientsPerPage && (
              <S.Pagination>
                <S.PaginationInfo>
                  Показано {((currentPage - 1) * clientsPerPage) + 1} - {Math.min(currentPage * clientsPerPage, totalCount)} из {totalCount}
                </S.PaginationInfo>
                <S.PaginationControls>
                  <S.PaginationButton
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Назад
                  </S.PaginationButton>
                  <S.PaginationPageInfo>
                    Страница {currentPage} из {Math.ceil(totalCount / clientsPerPage)}
                  </S.PaginationPageInfo>
                  <S.PaginationButton
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= Math.ceil(totalCount / clientsPerPage)}
                  >
                    Вперед
                  </S.PaginationButton>
                </S.PaginationControls>
              </S.Pagination>
            )}
          </>
        )}
      </S.ClientsContent>

      {selectedClient && (
        <S.ClientDetailsPanel>
          <S.ClientDetailsHeader>
            <S.ClientDetailsTitle>Информация о клиенте</S.ClientDetailsTitle>
            <S.CloseButton onClick={() => setSelectedClient(null)}>×</S.CloseButton>
          </S.ClientDetailsHeader>
          <S.ClientDetailsContent>
            <S.DetailsSection>
              <S.DetailsSectionTitle>Основная информация</S.DetailsSectionTitle>
              <S.DetailsRow>
                <S.DetailsLabel>Название:</S.DetailsLabel>
                <S.DetailsValue>{selectedClient.name}</S.DetailsValue>
              </S.DetailsRow>
              <S.DetailsRow>
                <S.DetailsLabel>Тип:</S.DetailsLabel>
                <S.DetailsValue>{getClientTypeLabel(selectedClient.type)}</S.DetailsValue>
              </S.DetailsRow>
              {selectedClient.uid && (
                <S.DetailsRow>
                  <S.DetailsLabel>UID:</S.DetailsLabel>
                  <S.DetailsValue>{selectedClient.uid}</S.DetailsValue>
                </S.DetailsRow>
              )}
              {selectedClient.region && (
                <S.DetailsRow>
                  <S.DetailsLabel>Регион:</S.DetailsLabel>
                  <S.DetailsValue>{selectedClient.region}</S.DetailsValue>
                </S.DetailsRow>
              )}
              {selectedClient.externalSite && (
                <S.DetailsRow>
                  <S.DetailsLabel>Сайт:</S.DetailsLabel>
                  <S.DetailsValue>{getSiteLabel(selectedClient.externalSite)}</S.DetailsValue>
                </S.DetailsRow>
              )}
            </S.DetailsSection>

            <S.DetailsSection>
              <S.DetailsSectionTitle>Контакты</S.DetailsSectionTitle>
              {getAllContacts(selectedClient).map(contact => (
                <S.ContactCard key={contact.id}>
                  <S.ContactCardHeader>
                    <S.ContactName>{contact.name || 'Без имени'}</S.ContactName>
                    {contact.id === selectedClient.mainContact?.id && (
                      <S.MainContactBadge>Основной</S.MainContactBadge>
                    )}
                  </S.ContactCardHeader>
                  <S.ContactInfo>
                    <S.ContactEmail>{contact.email}</S.ContactEmail>
                    {contact.phone && <S.ContactPhone>{contact.phone}</S.ContactPhone>}
                  </S.ContactInfo>
                </S.ContactCard>
              ))}
            </S.DetailsSection>

            <S.ToolsSection>
              <S.ToolsGrid>
                <S.ToolButton onClick={handleEmailClick}>
                  <S.ToolIcon><S3Icon iconName={"Mail"} /></S.ToolIcon>
                  <S.ToolLabel>Email</S.ToolLabel>
                </S.ToolButton>
                <S.ToolButton onClick={handleTicketsClick}>
                  <S.ToolIcon><S3Icon iconName={"History"} /></S.ToolIcon>
                  <S.ToolLabel>История обращений</S.ToolLabel>
                </S.ToolButton>
                <S.ToolButton onClick={handleCreateTicketClick}>
                  <S.ToolIcon><S3Icon iconName={"Tickets"} /></S.ToolIcon>
                  <S.ToolLabel>Создать тикет</S.ToolLabel>
                </S.ToolButton>
                <S.ToolButton onClick={handleEditClick}>
                  <S.ToolIcon><S3Icon iconName={"LedIconEditor"} /></S.ToolIcon>
                  <S.ToolLabel>Редактировать</S.ToolLabel>
                </S.ToolButton>
                <HudleStaffActions
                  variant="tools"
                  clientId={selectedClient.id}
                  uid={selectedClient.uid}
                  externalSite={selectedClient.externalSite}
                  email={selectedClient.mainContact?.email || getAllContacts(selectedClient)[0]?.email}
                  isBanned={Boolean(selectedClient.externalData?.user?.is_banned)}
                />
              </S.ToolsGrid>
            </S.ToolsSection>

            {selectedClient.externalData && Object.keys(selectedClient.externalData).length > 0 && (
              <S.DetailsSection>
                <S.DetailsSectionTitle>Дополнительные данные</S.DetailsSectionTitle>
                <S.ExternalData>
                  <pre>{JSON.stringify(selectedClient.externalData, null, 2)}</pre>
                </S.ExternalData>
              </S.DetailsSection>
            )}
          </S.ClientDetailsContent>
        </S.ClientDetailsPanel>
      )}

      {selectedClient && (
        <>
          <SendEmailModal
            isOpen={isEmailModalOpen}
            onClose={() => setIsEmailModalOpen(false)}
            client={selectedClient}
            onTicketCreated={(ticketId) => navigate(`/tickets/${ticketId}`)}
          />
          <EditClientModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            client={selectedClient}
            onClientUpdated={handleClientUpdated}
          />
          <ClientTicketsModal
            isOpen={isTicketsModalOpen}
            onClose={() => setIsTicketsModalOpen(false)}
            client={selectedClient}
          />
        </>
      )}
    </S.ClientsContainer>
  );
};
